import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar } from '../../components/Avatar';
import { Icon } from '../../components/Icon';
import { Banner } from '../../components/Status';
import { approxYear } from '../../domain/dates';
import { EMPTY_MEMORY, memoryForEveryone } from '../../domain/memoryDots';
import { displayName } from '../../domain/names';
import { canOpenPersonEdit, canSeeReviewMarkers, personEditMode, personOwner } from '../../domain/permissions';
import { branchOf } from '../../domain/relationships';
import type { UUID } from '../../domain/types';
import { useI18n } from '../../i18n';
import { useLocalState } from '../../lib/hooks';
import { useApp } from '../../app/AppState';
import { useRouter } from '../../app/router';
import { useToday } from '../people/WhereLine';
import { occasionText } from '../../components/Occasions';
import { activeVisit } from '../../domain/places';
import { FindPersonDialog } from './FindPersonDialog';
import { AVATAR_DY, AVATAR_R, buildEdges, layoutTree, NODE_H, NODE_W, type TreeLayout } from './layout';
import { layoutTreeByAge } from './layoutByAge';
import { PersonBubble } from './PersonBubble';
import { zoomBandOf, type ZoomBand } from './zoom';
import { relaxBands, relaxRows, type BandNode, type RowNode } from './treeRelax';
import { useSettledPositions, type Point } from './useSettledPositions';
import { ShabbatCandles } from '../../components/ShabbatCandles';
import { TreeCanvas, type CanvasHandle, type Viewport } from './TreeCanvas';

type Scope = 'me' | 'branch' | 'all';
/** Generation rows (the classic chart) or height by birth year (see layoutByAge.ts). Remembered on this device. */
type Arrangement = 'generations' | 'age';

/** A round floating icon button with a tooltip and an accessible name — the only kind of control on the tree. */
function StripButton({
  icon, label, on, disabled, mark, onClick, className = '', assist,
}: {
  icon: string; label: string; on?: boolean; disabled?: boolean; mark?: boolean; onClick: () => void; className?: string;
  /** Anchor id, so the assistant can light this one up (assistant/anchors.ts). */
  assist?: string;
}) {
  return (
    <button
      type="button"
      className={`fab-icon ${on ? 'is-on' : ''} ${className}`}
      data-assist={assist}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={on === undefined ? undefined : on}
    >
      <Icon name={icon} />
      {mark ? <span className="dotmark" aria-hidden="true" /> : null}
    </button>
  );
}

/*
 * How the tree keeps its footing when the family is large.
 *
 * None of this does anything for a family of thirty — everything is drawn,
 * everything drifts, exactly as it always has. It is what stops a family of a
 * thousand from being a thousand buttons, a thousand photographs and a thousand
 * animations on a phone that is showing forty of them.
 */
/** Up to this many people the whole tree is simply mounted; past it, only what is near the screen. */
const CULL_FROM = 150;
/** How far past the edge of the screen is kept mounted, as a share of the screen — so a drag never outruns the circles. */
const CULL_MARGIN = 0.35;
/** More circles than this on the screen at once and they stop drifting and lose their glow: see tree.css `[data-crowded]`. */
const CROWDED_FROM = 70;
/** Past this many people circles go straight to their places instead of riding a spring there (a frame of spring is a render of the tree). */
const SPRING_MAX = 150;

/** A box in the tree's own coordinates. */
type Box = { x0: number; y0: number; x1: number; y1: number };
const within = (outer: Box, inner: Box) => inner.x0 >= outer.x0 && inner.y0 >= outer.y0 && inner.x1 <= outer.x1 && inner.y1 <= outer.y1;

/**
 * The decade labels down the edge of the by-age tree. They follow the camera
 * every frame, so they are a component of their own with the camera as *their*
 * state: following it re-renders a dozen spans, not the family.
 */
function YearScale({ marks, follow, start }: { marks: Array<{ year: number; y: number }>; follow: (l: (v: Viewport) => void) => () => void; start: () => Viewport | undefined }) {
  const [camera, setCamera] = useState<Viewport | undefined>(start);
  useEffect(() => follow(setCamera), [follow]);
  if (!camera) return null;
  return (
    <div className="tree-year-scale" aria-hidden="true">
      {marks.map((m) => (
        <span key={m.year} style={{ top: m.y * camera.scale + camera.y }}>{m.year}</span>
      ))}
    </div>
  );
}

export function TreeScreen() {
  const { t, lang } = useI18n();
  const { search } = useRouter();
  const { people, peopleById, relationships, graph, stories, contributions, session, actor, me, links, portraits, stars, treeSettings, moments, places, reducedMotionActive, dataLoading, shabbatIds, occasionToday, awaitingRecognition, has, openMemories } = useApp();
  const today = useToday();
  const canLegend = has('tree.legend');
  const visitingIds = useMemo(() => new Set(people.filter((p) => activeVisit(places, p.id, today)).map((p) => p.id)), [people, places, today]);

  // Not persisted on purpose: every visit opens on the whole family, centred on you.
  const [scope, setScope] = useState<Scope>('all');
  const [storedArrangement, setArrangement] = useLocalState<Arrangement>('edry.tree.arrangement', 'generations');
  const arrangement: Arrangement = storedArrangement === 'age' ? 'age' : 'generations';
  const [selectedId, setSelectedId] = useState<UUID | null>(null);
  const [focusId, setFocusId] = useState<UUID | null>(null);
  const [showFind, setShowFind] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  /** True when the camera has drifted so far that no circle is on screen. */
  const [lost, setLost] = useState(false);
  /** How closely the tree is being looked at — the only thing about the camera a circle needs to know. */
  const [zoom, setZoom] = useState<ZoomBand>('near');
  /** The part of a large tree that is mounted, in the tree's own coordinates. Null: all of it. */
  const [mounted, setMounted] = useState<Box | null>(null);
  /** Too many circles on the screen to keep them all drifting. */
  const [crowded, setCrowded] = useState(false);
  /** Whoever wants the camera frame by frame (the by-age year scale) — told directly, not through this screen's state. */
  const cameraFollowers = useRef(new Set<(v: Viewport) => void>());
  const followCamera = useCallback((l: (v: Viewport) => void) => {
    cameraFollowers.current.add(l);
    return () => {
      cameraFollowers.current.delete(l);
    };
  }, []);
  const canvas = useRef<CanvasHandle | null>(null);
  const didInitialFit = useRef(false);

  const rootId = focusId ?? me?.id ?? people[0]?.id ?? null;

  // Deep link: /tree?focus=<id>. It asks for one person, so it also claims the
  // opening camera move — otherwise the initial framing below would fit the
  // whole family over the top of it.
  useEffect(() => {
    const f = search.get('focus');
    if (f && peopleById.has(f)) {
      setFocusId(f);
      setSelectedId(f);
      setScope('branch');
      setPendingView({ kind: 'branch', id: f });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Everyone is always laid out. "My branch" does not remove the rest of the
  // family — it dims them and frames the branch, so nobody loses the tree.
  const scopeIds = useMemo(() => new Set(people.map((p) => p.id)), [people]);
  const branchIds = useMemo(() => (scope === 'branch' && rootId ? branchOf(graph, rootId) : null), [scope, rootId, graph]);

  const memoryByPerson = useMemo(
    () => memoryForEveryone(people, stories, contributions, portraits, moments, links, treeSettings),
    [people, stories, contributions, portraits, treeSettings, moments, links],
  );

  const visibleIds = useMemo(() => people.filter((p) => scopeIds.has(p.id)).map((p) => p.id), [people, scopeIds]);

  // Oldest → youngest along a row (a couple counts as the average of the two).
  const birthYear = useCallback((id: UUID) => approxYear(peopleById.get(id)?.birth), [peopleById]);
  const layout = useMemo(
    () => (arrangement === 'age' ? layoutTreeByAge(graph, visibleIds, relationships, birthYear) : layoutTree(graph, visibleIds, relationships, birthYear)),
    [arrangement, graph, visibleIds, relationships, birthYear],
  );
  /** Dimmed = outside the branch you are looking at. Nothing else ever fades a relative. */
  const isDim = useCallback((id: UUID) => (branchIds ? !branchIds.has(id) : false), [branchIds]);
  const ownedIds = useMemo(() => new Set(links.filter((l) => l.status === 'active').map((l) => l.personId)), [links]);

  /** 1 = resting size; grows with fresh activity and settles back (admin-tunable). */
  const nodeScaleOf = useCallback(
    (id: UUID) => treeSettings.nodeScaleDefault + (treeSettings.nodeScaleMax - treeSettings.nodeScaleDefault) * (memoryByPerson.get(id)?.freshness ?? 0),
    [treeSettings, memoryByPerson],
  );
  /**
   * Live spacing: the layout leaves room for a circle at its resting size only.
   * Each circle's real footprint (its size right now plus the "you" / star ring)
   * is fed to the row relaxer, which nudges neighbours apart by exactly the
   * overlap; the spring hook then eases everyone there. Selection is left out
   * on purpose, so tapping a circle never shifts the tree.
   */
  const footprints = useMemo<Array<RowNode & BandNode>>(() => {
    const out: Array<RowNode & BandNode> = [];
    for (const id of visibleIds) {
      const n = layout.nodes.get(id);
      if (!n) continue;
      const isMe = me?.id === id;
      const starred = stars.has(id);
      const ring = isMe && starred ? 10 : isMe ? 7 : starred ? 4 : 0;
      const scale = nodeScaleOf(id);
      const radius = AVATAR_R * scale + ring;
      // Vertical extent (by-age arrangement): top of the circle to the bottom of the name, which a grown circle pushes down.
      out.push({ id, x: n.x, y: n.y, radius, halfW: radius, top: n.y + AVATAR_DY - radius, bottom: n.y + NODE_H / 2 + (scale - 1) * AVATAR_R });
    }
    return out;
  }, [visibleIds, layout, me, stars, nodeScaleOf]);
  const targets = useMemo(() => {
    const xs = arrangement === 'age' ? relaxBands(footprints, 14) : relaxRows(footprints, 14);
    const out = new Map<UUID, Point>();
    for (const f of footprints) out.set(f.id, { x: xs.get(f.id) ?? f.x, y: f.y });
    return out;
  }, [arrangement, footprints]);
  const shown = useSettledPositions(layout.nodes, targets, reducedMotionActive || layout.nodes.size > SPRING_MAX);
  const edges = useMemo(() => buildEdges(graph, shown, relationships), [graph, shown, relationships]);

  /*
   * What the camera moving means for this screen — worked out at most once a
   * frame (TreeCanvas sees to that), and almost always ending in "nothing to
   * re-render": every `set` below is handed the value it already has, and React
   * stops there. The tree is only drawn again when a zoom band is crossed, when
   * the screen reaches the edge of what is mounted, or when it becomes (or stops
   * being) crowded.
   */
  const onViewport = useCallback(
    (v: Viewport) => {
      for (const follower of cameraFollowers.current) follower(v);
      setZoom(zoomBandOf(v.scale));
      const size = canvas.current?.getSize();
      if (!size || !size.width || !layout.nodes.size) return;
      // What the screen is showing, in the tree's own coordinates.
      const seen: Box = { x0: -v.x / v.scale, y0: -v.y / v.scale, x1: (size.width - v.x) / v.scale, y1: (size.height - v.y) / v.scale };
      const edge = 60 / v.scale;
      let onScreen = 0;
      for (const n of layout.nodes.values()) {
        if (n.x > seen.x0 - edge && n.x < seen.x1 + edge && n.y > seen.y0 - edge && n.y < seen.y1 + edge) onScreen++;
      }
      setLost(onScreen === 0);
      // With a little give, so a tree that sits right at the line does not start and stop drifting as it is nudged.
      setCrowded((was) => onScreen > (was ? CROWDED_FROM - 15 : CROWDED_FROM));
      if (layout.nodes.size <= CULL_FROM) {
        setMounted(null);
        return;
      }
      /*
       * Keep what is mounted until the screen (and one circle's width around it)
       * reaches its edge; then mount afresh around the screen. And let go of a box
       * that has become far bigger than the screen needs — the one made while
       * looking at the whole family, once somebody has leaned in on six people:
       * otherwise those six are dragged about with four hundred others in tow.
       */
      const needed: Box = { x0: seen.x0 - NODE_W, y0: seen.y0 - NODE_H, x1: seen.x1 + NODE_W, y1: seen.y1 + NODE_H };
      const mx = (seen.x1 - seen.x0) * CULL_MARGIN + NODE_W;
      const my = (seen.y1 - seen.y0) * CULL_MARGIN + NODE_H;
      const ideal: Box = { x0: seen.x0 - mx, y0: seen.y0 - my, x1: seen.x1 + mx, y1: seen.y1 + my };
      const area = (b: Box) => (b.x1 - b.x0) * (b.y1 - b.y0);
      setMounted((was) => (was && within(was, needed) && area(was) <= area(ideal) * 4 ? was : ideal));
    },
    [layout],
  );
  // A new layout (somebody added, the arrangement switched) is a new answer to all of the above, camera moved or
  // not — and so is a screen that has changed shape (a phone turned on its side shows a different part of the tree).
  useEffect(() => {
    const look = () => {
      const v = canvas.current?.getViewport();
      if (v) onViewport(v);
    };
    look();
    window.addEventListener('resize', look);
    return () => window.removeEventListener('resize', look);
  }, [onViewport]);

  /*
   * Whether a circle at this spot is part of what is mounted. A small family is
   * mounted whole. A large one waits for the first box — a single empty frame —
   * rather than mounting a thousand circles in order to throw nine hundred away.
   */
  const large = layout.nodes.size > CULL_FROM;
  const isMounted = useCallback(
    (x: number, y: number) => (large ? !!mounted && x >= mounted.x0 && x <= mounted.x1 && y >= mounted.y0 && y <= mounted.y1 : true),
    [large, mounted],
  );
  /*
   * A line is kept when the box between its two ends touches what is mounted —
   * not merely when one of its ends is. A parent far off to the left with a
   * child far off to the right has a line running straight across the screen,
   * and it must not blink out because both circles are out of sight. (The curve
   * never leaves that box: its control points are its own corners.)
   */
  const drawnEdges = useMemo(() => {
    if (!large) return edges;
    if (!mounted) return [];
    return edges.filter((e) => {
      const a = shown.get(e.from);
      const b = shown.get(e.to);
      if (!a || !b) return false;
      return Math.max(a.x, b.x) >= mounted.x0 && Math.min(a.x, b.x) <= mounted.x1 && Math.max(a.y, b.y) + AVATAR_DY >= mounted.y0 - NODE_H && Math.min(a.y, b.y) + AVATAR_DY <= mounted.y1 + NODE_H;
    });
  }, [edges, shown, mounted, large]);
  const labelBadgeMore = useCallback((n: number) => t('tree.badge.more', { n }), [t]);

  /**
   * Camera moves are queued and applied *after* the new layout is computed —
   * fitting against a stale layout would leave the tree off-screen.
   */
  const [pendingView, setPendingView] = useState<{ kind: 'fit' } | { kind: 'center'; id: UUID } | { kind: 'branch'; id: UUID } | null>(null);
  useEffect(() => {
    if (!pendingView || !layout.nodes.size) return;
    const animate = !reducedMotionActive && didInitialFit.current;
    // The whole family — or, when it is too large to be seen whole, as much of it as there is around you.
    if (pendingView.kind === 'fit') canvas.current?.fit(layout, 90, animate, (me && layout.nodes.get(me.id)) || undefined);
    else if (pendingView.kind === 'branch') {
      const ids = branchOf(graph, pendingView.id);
      const bounds = boundsOf(layout, ids);
      if (bounds && ids.size > 1) canvas.current?.fit(bounds, 110, animate);
      else {
        const node = layout.nodes.get(pendingView.id);
        if (node) canvas.current?.centerOn(node.x, node.y, 0.85, animate);
      }
    } else {
      const node = layout.nodes.get(pendingView.id);
      if (node) canvas.current?.centerOn(node.x, node.y, Math.max(0.7, canvas.current.getViewport().scale), animate);
      else canvas.current?.fit(layout, 90, animate, (me && layout.nodes.get(me.id)) || undefined);
    }
    didInitialFit.current = true;
    setPendingView(null);
  }, [pendingView, layout, reducedMotionActive, graph, me]);

  const selected = selectedId ? peopleById.get(selectedId) ?? null : null;
  const showPendingFor = actor && canSeeReviewMarkers(actor);

  /**
   * Opening view: the whole family, in one screen.
   *
   * It used to open centred on you, close in. Jay: the first thing the app
   * shows should be the tree — you can always find yourself again with "Centre
   * on me", but you cannot see the shape of a family from inside it, and a
   * relative who opens the app to a close-up of their own circle has no idea
   * how much else is there. A `?focus=` link still arrives framed on that
   * person's branch: it has already queued its own camera move above.
   */
  useEffect(() => {
    if (didInitialFit.current || !layout.nodes.size || pendingView) return;
    setPendingView({ kind: 'fit' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.nodes.size]);

  /**
   * "Centre on me" moves the camera to your circle and does nothing else — it
   * is a way of finding yourself again, not a way of opening your profile.
   * Anything already open is put away so the view is clear when you arrive.
   */
  const handleCenterMe = () => {
    if (!me) return;
    setFocusId(me.id);
    setSelectedId(null);
    setPendingView({ kind: 'center', id: me.id });
  };

  const handleScope = (next: Scope) => {
    setScope(next === 'me' ? 'branch' : next);
    if (next === 'me' && me) setFocusId(me.id);
    if (next === 'all') {
      setFocusId(null);
      setPendingView({ kind: 'fit' });
    } else setPendingView(rootId ? { kind: 'branch', id: rootId } : { kind: 'fit' });
  };

  const clearSelection = useCallback(() => setSelectedId(null), []);

  const pickFromSearch = (id: UUID) => {
    setSelectedId(id);
    setPendingView({ kind: 'center', id });
  };

  /**
   * Tapping a circle opens that person.
   *
   * Not a card over the tree any more: the circle itself travels to the middle
   * of the screen and everything of theirs — their pictures, the stories they
   * are in, the faces the family has chosen for them — comes out around it, the
   * same shape a day on the timeline opens into. The rect of the circle you
   * pressed goes with it, so the movement is that circle growing rather than a
   * new screen arriving, and Back puts it down exactly where it was.
   *
   * They stay selected underneath, so closing the constellation leaves the tree
   * showing where you were.
   */
  const openCircle = useCallback(
    (id: UUID) => {
      setSelectedId(id);
      const el = document.querySelector(`[data-node="${id}"] .bubble-avatar-wrap`) ?? document.querySelector(`[data-node="${id}"]`);
      const r = el?.getBoundingClientRect();
      openMemories({ kind: 'person', id }, r ? { x: r.left + r.width / 2, y: r.top + r.height / 2, size: r.width } : null);
    },
    [openMemories],
  );

  if (dataLoading) {
    return (
      <div className="tree-screen">
        <div className="empty">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="tree-screen">
      {/* One short strip of pictures: centre on me, the whole family, find someone, how it is arranged. */}
      <div className="tree-strip" role="toolbar" aria-label={t('tree.title')}>
        <StripButton icon="center" label={t('tree.centerMe')} assist="tree.centerMe" onClick={handleCenterMe} disabled={!me} />
        {/* Not lit while the whole family is showing: that is where every visit starts, so
            colouring it says nothing and only makes one button in the row shout. It does go
            gold while a branch is framed — then it is the way back, and worth noticing. */}
        <StripButton icon="tree" label={t('tree.entireFamily')} assist="tree.entireFamily" className={scope === 'branch' ? 'is-attention' : ''} onClick={() => handleScope('all')} />
        <StripButton icon="search" label={t('tree.find')} assist="tree.find" onClick={() => setShowFind(true)} />
        <StripButton icon="history" label={arrangement === 'age' ? t('tree.arrange.generations') : t('tree.arrange.age')} assist="tree.arrange" on={arrangement === 'age'} onClick={() => setArrangement(arrangement === 'age' ? 'generations' : 'age')} />
        {canLegend && <StripButton icon="info" label={t('tree.legend')} assist="tree.legend" on={showLegend} onClick={() => setShowLegend((v) => !v)} />}
      </div>

      {scope === 'branch' && (
        <button type="button" className="tree-back-pill" onClick={() => handleScope('all')}>
          <Icon name="tree" />
          <span>{t('tree.showEntireFamily')}</span>
        </button>
      )}

      {!me && session && (
        <div className="tree-banner">
          <Banner kind="info" title={t('tree.noMe')}>
            <button type="button" className="btn btn-sm btn-secondary mt-2" onClick={() => setShowFind(true)}>
              {t('tree.noMe.action')}
            </button>
          </Banner>
        </div>
      )}

      {(
        <div className="tree-stage" data-crowded={crowded ? 'true' : undefined}>
          <TreeCanvas
            ref={canvas}
            reducedMotion={reducedMotionActive}
            onViewportChange={onViewport}
            ariaLabel={`${t('tree.title')}. ${t('tree.keyboardHint')}`}
            onBackgroundClick={clearSelection}
          >
            {(
              <>
                <svg
                  className="tree-edges"
                  style={{ left: layout.minX - 200, top: layout.minY - 200, width: layout.width + 400, height: layout.height + 400 }}
                  viewBox={`${layout.minX - 200} ${layout.minY - 200} ${layout.width + 400} ${layout.height + 400}`}
                  aria-hidden="true"
                >
                  {/* By age: a faint line per decade across the tree, so height reads as a year (the labels stick to the screen edge, below). */}
                  {layout.yearMarks?.map((m) => (
                    <line key={m.year} className="tree-year" x1={layout.minX - 60} y1={m.y} x2={layout.minX + layout.width + 60} y2={m.y} />
                  ))}
                  {drawnEdges.map((e) => {
                    const related = selectedId ? e.from === selectedId || e.to === selectedId : false;
                    const faded = isDim(e.from) || isDim(e.to);
                    return (
                      <path
                        key={e.id}
                        d={e.d}
                        className={`tree-edge ${e.kind} ${e.dashed ? 'dashed' : ''} ${e.pending ? 'pending' : ''} ${related ? 'related' : ''} ${faded ? 'faded' : ''}`}
                      />
                    );
                  })}
                  {/* The little ring on a marriage is one of the trimmings that go when the tree is seen from far off. */}
                  {(zoom === 'near' || zoom === 'close' ? drawnEdges : [])
                    .filter((e) => e.kind === 'partner' && e.relationship.subtype === 'married')
                    .map((e) => (
                      <circle key={`m-${e.id}`} cx={e.mid.x} cy={e.mid.y} r={6} className="edge-marker" />
                    ))}
                </svg>
                {visibleIds.map((id) => {
                  const node = layout.nodes.get(id);
                  const at = shown.get(id);
                  const person = peopleById.get(id);
                  if (!node || !person) return null;
                  // A large tree only mounts what is near the screen (see `onViewport`).
                  if (!isMounted(at?.x ?? node.x, at?.y ?? node.y)) return null;
                  const memory = memoryByPerson.get(id) ?? EMPTY_MEMORY;
                  return (
                    <PersonBubble
                      key={id}
                      person={person}
                      lang={lang}
                      x={at?.x ?? node.x}
                      y={at?.y ?? node.y}
                      zoom={zoom}
                      isMe={me?.id === id}
                      isSelected={selectedId === id}
                      dimmed={isDim(id)}
                      memory={memory}
                      showPending={!!showPendingFor && person.reviewStatus === 'pending'}
                      awaitingRecognition={awaitingRecognition.has(id)}
                      reducedMotion={reducedMotionActive}
                      floatSeed={node.floatSeed}
                      unclaimed={!ownedIds.has(id)}
                      starred={stars.has(id)}
                      visiting={visitingIds.has(id)}
                      shabbat={shabbatIds.has(id)}
                      labelShabbat={t('shabbat.now')}
                      occasion={occasionToday.get(id)?.kind}
                      labelOccasion={occasionToday.has(id) ? occasionText(occasionToday.get(id)!, lang, t, peopleById) : undefined}
                      nodeScale={nodeScaleOf(id)}
                      onSelect={openCircle}
                      labelStories={t('tree.stories', { n: memory.total })}
                      labelPending={t('tree.pendingMarker')}
                      labelYou={t('tree.card.you')}
                      labelUnclaimed={t('tree.unclaimed')}
                      labelMemorial={t('common.deceasedMark')}
                      memorialMark={t('common.memorialMark')}
                      labelBadgeStory={t('tree.badge.story')}
                      labelBadgePhoto={t('tree.badge.photo')}
                      labelBadgeMore={labelBadgeMore}
                    />
                  );
                })}
              </>
            )}
          </TreeCanvas>


          {arrangement === 'age' && layout.yearMarks && <YearScale marks={layout.yearMarks} follow={followCamera} start={() => canvas.current?.getViewport()} />}

          <p className="tree-hint desktop-only">{t('tree.hint')}</p>

          {lost && (
            <button type="button" className="tree-lost" onClick={() => setPendingView(me ? { kind: 'center', id: me.id } : { kind: 'fit' })}>
              <Icon name="tree" />
              <span>{t('tree.lost')}</span>
            </button>
          )}

          {showLegend && canLegend && (
            <div className="tree-legend" role="note" aria-label={t('tree.legend')}>
              <span className="k"><i className="parent" />{t('tree.legend.parent')}</span>
              <span className="k"><i className="partner" />{t('tree.legend.partner')}</span>
              <span className="k"><i className="former" />{t('tree.legend.former')}</span>
              <span className="k"><i className="other" />{t('tree.legend.other')}</span>
              {showPendingFor ? <span className="k"><i className="pending" />{t('tree.legend.pending')}</span> : null}
              <span className="k"><i className="memory" />{t('tree.legend.memory')}</span>
              <span className="k"><i className="you" />{t('tree.legend.you')}</span>
              <span className="k"><i className="unclaimed"><Icon name="person" /></i>{t('tree.legend.unclaimed')}</span>
              <span className="k"><i className="unclaimed memorial"><span className="memorial-mark" dir="rtl" lang="he">{t('common.memorialMark')}</span></i>{t('tree.legend.memorial')}</span>
              <span className="k"><i className="dim" />{t('tree.legend.dim')}</span>
              <span className="k"><i className="candles"><ShabbatCandles still /></i>{t('tree.legend.shabbat')}</span>
              <span className="k"><i className="star" />{t('tree.legend.star')}</span>
              <span className="k"><i className="visit" />{t('tree.legend.visit')}</span>
              <span className="k"><i className="occasion"><Icon name="cake" /></i>{t('tree.legend.occasion')}</span>
              {arrangement === 'age' ? <span className="k"><i className="age">1950</i>{t('tree.legend.age')}</span> : null}
            </div>
          )}
        </div>
      )}


      <FindPersonDialog open={showFind} onClose={() => setShowFind(false)} people={people} onPick={pickFromSearch} />
    </div>
  );
}

function boundsOf(layout: TreeLayout, ids: Set<UUID>): { minX: number; minY: number; width: number; height: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const id of ids) {
    const n = layout.nodes.get(id);
    if (!n) continue;
    minX = Math.min(minX, n.x - 90);
    maxX = Math.max(maxX, n.x + 90);
    minY = Math.min(minY, n.y - 95);
    maxY = Math.max(maxY, n.y + 95);
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, width: maxX - minX, height: maxY - minY };
}

/*
 * The quick card a tapped circle opens moved to ./PersonCard so that the app
 * can show it over any screen (a story's faces, the timeline) without pulling
 * the whole tree in. Re-exported because the tree is where it was born.
 */
export { PersonCard } from './PersonCard';
